import { useCallback, useEffect, useMemo, useState } from 'react'
import { ExternalLink, FileJson } from 'lucide-react'
import {
  ApiGatewayKeyCreateDialog,
  ApiGatewayManagementWorkspace,
  ApiGatewaySecretRevealDialog,
  type ApiGatewayCreateDraft,
  type ApiGatewayCreateValidation,
  type ApiGatewayKeyRecord,
  type ApiGatewayManagementAction,
  type ApiGatewayManagementNotice,
  type ApiGatewayManagementStat,
  type ApiGatewayPermissionFamilyOption,
} from '@workspace/silk-ui'
import { toast } from '@workspace/ui'
import { useScope } from '@workspace/scope-context'

import {
  createApiGatewayKey,
  getApiGatewayOpenApiDocsUrl,
  getApiGatewayOpenApiJsonUrl,
  listApiGatewayKeys,
  listApiGatewayPermissionFamilies,
  revokeApiGatewayKey,
  type ApiGatewayKeyRecordResponse,
  type ApiGatewayPermissionFamily,
} from '@/lib/api-gateway-management'
import { summarizeApiGatewayKeyPermissions } from '@/lib/api-gateway-permissions'
import { useApiGatewayAccess } from '@/lib/useApiGatewayAccess'

function createEmptyDraft(): ApiGatewayCreateDraft {
  return {
    displayName: '',
    permissions: [],
    expiresAt: '',
    allowedIps: [],
  }
}

function formatDateLabel(value?: string | null): string {
  if (!value) {
    return 'Does not expire'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}

function formatLastUsedLabel(value?: string | null): string {
  if (!value) {
    return 'Never used'
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }
  return date.toLocaleString()
}

function buildAllowedIpSummary(values: string[]): string {
  if (!values.length) {
    return 'No IP allowlist'
  }
  if (values.length === 1) {
    return values[0]
  }
  return `${values[0]} +${values.length - 1} more`
}

function buildPermissionFamilyOptions(families: ApiGatewayPermissionFamily[]): ApiGatewayPermissionFamilyOption[] {
  return families.map(family => ({
    id: family.id,
    label: family.label,
    description: family.description,
    readPermission: family.read_permission,
    managePermission: family.manage_permission,
    pathPrefixes: family.path_prefixes ?? [],
  }))
}

function buildKeyRecord(record: ApiGatewayKeyRecordResponse, families: ApiGatewayPermissionFamily[]): ApiGatewayKeyRecord {
  const permissionSummary = summarizeApiGatewayKeyPermissions(record.permissions, record.scopes, families)

  return {
    id: record.id,
    displayName: record.display_name,
    prefix: record.prefix,
    statusLabel: record.status,
    statusTone: record.status === 'active' ? 'success' : record.status === 'revoked' ? 'danger' : 'warning',
    description: record.linked_principal_status
      ? `Linked principal: ${record.linked_principal_status}`
      : null,
    permissions: permissionSummary.permissions,
    permissionSummary: permissionSummary.summary,
    allowedIpSummary: buildAllowedIpSummary(record.allowed_ips ?? []),
    createdAtLabel: formatDateLabel(record.created_at),
    lastUsedAtLabel: formatLastUsedLabel(record.last_used_at),
    expiresAtLabel: record.expires_at ? formatDateLabel(record.expires_at) : 'Does not expire',
    metaItems: [
      { id: 'created', label: 'Created', value: formatDateLabel(record.created_at) },
      { id: 'last-used', label: 'Last used', value: formatLastUsedLabel(record.last_used_at) },
      { id: 'expires', label: 'Expires', value: record.expires_at ? formatDateLabel(record.expires_at) : 'Does not expire' },
    ],
  }
}

function validateDraft(draft: ApiGatewayCreateDraft): ApiGatewayCreateValidation {
  const next: ApiGatewayCreateValidation = {}

  if (!draft.displayName.trim()) {
    next.displayName = 'Display name is required.'
  }
  if (draft.permissions.length === 0) {
    next.permissions = 'Choose at least one permission family access level.'
  }
  if (draft.expiresAt) {
    const expiresAt = new Date(draft.expiresAt)
    if (Number.isNaN(expiresAt.getTime())) {
      next.expiresAt = 'Expiration must be a valid date.'
    }
  }
  if (draft.allowedIps.some(value => !value.trim())) {
    next.allowedIps = 'Allowed IP entries cannot be blank.'
  }

  return next
}

async function copyText(value: string): Promise<void> {
  await navigator.clipboard.writeText(value)
}

export default function AutomationPage() {
  const { activeTenant, activeOrganization } = useScope()
  const apiGatewayAccess = useApiGatewayAccess()

  const [keys, setKeys] = useState<ApiGatewayKeyRecordResponse[]>([])
  const [permissionFamilies, setPermissionFamilies] = useState<ApiGatewayPermissionFamily[]>([])
  const [loading, setLoading] = useState(false)
  const [permissionFamiliesLoading, setPermissionFamiliesLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [permissionFamilyError, setPermissionFamilyError] = useState<string | null>(null)
  const [selectedKeyId, setSelectedKeyId] = useState<string | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [createDraft, setCreateDraft] = useState<ApiGatewayCreateDraft>(() => createEmptyDraft())
  const [createValidation, setCreateValidation] = useState<ApiGatewayCreateValidation>({})
  const [submitting, setSubmitting] = useState(false)
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null)
  const [secretState, setSecretState] = useState<{
    open: boolean
    displayName: string
    prefix: string
    secret: string
    copied: boolean
  }>({
    open: false,
    displayName: '',
    prefix: '',
    secret: '',
    copied: false,
  })

  const loadKeys = useCallback(async () => {
    if (!apiGatewayAccess.accessEnabled || !activeTenant?.id) {
      setKeys([])
      setError(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    try {
      const nextKeys = await listApiGatewayKeys()
      setKeys(nextKeys)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load API gateway keys.')
      setKeys([])
    } finally {
      setLoading(false)
    }
  }, [activeTenant?.id, apiGatewayAccess.accessEnabled])

  const loadPermissionFamilies = useCallback(async () => {
    if (!apiGatewayAccess.accessEnabled || !activeTenant?.id) {
      setPermissionFamilies([])
      setPermissionFamilyError(null)
      setPermissionFamiliesLoading(false)
      return
    }

    setPermissionFamiliesLoading(true)
    setPermissionFamilyError(null)
    try {
      const nextFamilies = await listApiGatewayPermissionFamilies()
      setPermissionFamilies(nextFamilies)
    } catch (err) {
      setPermissionFamilyError(err instanceof Error ? err.message : 'Failed to load API Gateway permission families.')
      setPermissionFamilies([])
    } finally {
      setPermissionFamiliesLoading(false)
    }
  }, [activeTenant?.id, apiGatewayAccess.accessEnabled])

  useEffect(() => {
    void loadKeys()
  }, [loadKeys])

  useEffect(() => {
    void loadPermissionFamilies()
  }, [loadPermissionFamilies])

  useEffect(() => {
    if (!keys.length) {
      setSelectedKeyId(null)
      return
    }

    if (selectedKeyId && keys.some(key => key.id === selectedKeyId)) {
      return
    }

    setSelectedKeyId(keys[0]?.id ?? null)
  }, [keys, selectedKeyId])

  const permissionFamilyOptions = useMemo(
    () => buildPermissionFamilyOptions(permissionFamilies),
    [permissionFamilies]
  )

  const mappedKeys = useMemo(
    () => keys.map(key => buildKeyRecord(key, permissionFamilies)),
    [keys, permissionFamilies]
  )

  const notices = useMemo<ApiGatewayManagementNotice[]>(() => {
    if (!activeTenant?.id) {
      return [
        {
          id: 'no-tenant',
          title: 'Select a tenant',
          description: 'API Gateway keys require an active tenant context in the portal shell.',
          tone: 'info',
        },
      ]
    }

    if (!apiGatewayAccess.apiGatewayFeatureEnabled) {
      return [
        {
          id: 'feature-disabled',
          title: 'API Gateway is not enabled for this tenant',
          description: 'Ask an administrator to enable the API Gateway tenant feature before creating or viewing keys here.',
          tone: 'warning',
        },
      ]
    }

    if (!apiGatewayAccess.apiGatewayPermissionGranted) {
      return [
        {
          id: 'permission-denied',
          title: 'You do not have permissions',
          description: 'Your tenant membership does not include permission to manage API Gateway keys for this tenant.',
          tone: 'danger',
        },
      ]
    }

    const next: ApiGatewayManagementNotice[] = [
      {
        id: 'secret-once',
        title: 'Secrets are shown once',
        description: 'Copy new API Gateway keys when they are created. The secret portion cannot be retrieved again later.',
        tone: 'info',
      },
    ]

    if (permissionFamiliesLoading) {
      next.push({
        id: 'permissions-loading',
        title: 'Loading permission families',
        description: 'The gateway permission catalog is loading. Key creation becomes available when the catalog is ready.',
        tone: 'info',
      })
    } else if (permissionFamilyError) {
      next.push({
        id: 'permissions-error',
        title: 'Permission families unavailable',
        description: 'The permission catalog could not be loaded, so key creation is temporarily unavailable.',
        tone: 'danger',
      })
    } else if (permissionFamilies.length > 0) {
      next.push({
        id: 'permissions-catalog',
        title: 'Canonical gateway permissions',
        description: 'Assign access per route family such as catalog, pricebooks, discounts, commissions, quotes, orders, carts, and legacy compatibility.',
        tone: 'info',
      })
    }

    return next
  }, [
    activeTenant?.id,
    apiGatewayAccess.apiGatewayFeatureEnabled,
    apiGatewayAccess.apiGatewayPermissionGranted,
    permissionFamilies.length,
    permissionFamiliesLoading,
    permissionFamilyError,
  ])

  const stats = useMemo<ApiGatewayManagementStat[]>(() => {
    const activeCount = keys.filter(key => key.status === 'active').length
    const expiringCount = keys.filter(key => {
      if (!key.expires_at) {
        return false
      }
      const expiresAt = new Date(key.expires_at)
      const threshold = Date.now() + 1000 * 60 * 60 * 24 * 30
      return !Number.isNaN(expiresAt.getTime()) && expiresAt.getTime() <= threshold
    }).length

    return [
      { id: 'active', label: 'Active keys', value: String(activeCount) },
      { id: 'expiring', label: 'Expiring soon', value: String(expiringCount) },
      { id: 'tenant', label: 'Tenant', value: activeTenant?.name ?? activeTenant?.id ?? 'None selected' },
      {
        id: 'feature',
        label: 'Feature',
        value: apiGatewayAccess.apiGatewayFeatureEnabled ? 'Enabled' : 'Disabled',
      },
    ]
  }, [activeTenant?.id, activeTenant?.name, apiGatewayAccess.apiGatewayFeatureEnabled, keys])

  const docsActions = useMemo<ApiGatewayManagementAction[]>(
    () => [
      {
        id: 'docs',
        label: 'Interactive docs',
        description: 'Open the portal-hosted developer console for interactive API exploration.',
        icon: <ExternalLink className="h-4 w-4" aria-hidden />,
      },
      {
        id: 'openapi',
        label: 'Raw OpenAPI',
        description: 'Raw OpenAPI JSON for generated clients and tooling.',
        icon: <FileJson className="h-4 w-4" aria-hidden />,
      },
    ],
    []
  )

  const openExternal = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [])

  const handleDocsAction = useCallback(
    (actionId: string) => {
      if (actionId === 'docs') {
        openExternal(getApiGatewayOpenApiDocsUrl())
        return
      }
      if (actionId === 'openapi') {
        openExternal(getApiGatewayOpenApiJsonUrl())
      }
    },
    [openExternal]
  )

  const handleCreateSubmit = useCallback(async () => {
    const validation = validateDraft(createDraft)
    if (Object.keys(validation).length > 0) {
      setCreateValidation(validation)
      return
    }

    setSubmitting(true)
    setCreateValidation({})
    try {
      const response = await createApiGatewayKey({
        display_name: createDraft.displayName.trim(),
        permissions: createDraft.permissions,
        scopes: [],
        allowed_ips: createDraft.allowedIps.map(value => value.trim()).filter(Boolean),
        expires_at: createDraft.expiresAt ? new Date(createDraft.expiresAt).toISOString() : null,
        metadata: {
          issued_from: 'app-portal',
        },
      })
      setCreateOpen(false)
      setCreateDraft(createEmptyDraft())
      setSecretState({
        open: true,
        displayName: response.key.display_name,
        prefix: response.key.prefix,
        secret: response.secret,
        copied: false,
      })
      toast.success('API Gateway key created.')
      await loadKeys()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create API Gateway key.'
      setCreateValidation({ general: message })
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }, [createDraft, loadKeys])

  const handleRevoke = useCallback(
    async (keyId: string) => {
      const target = keys.find(key => key.id === keyId)
      const label = target?.display_name ?? 'this key'
      if (!window.confirm(`Revoke ${label}? This cannot be undone.`)) {
        return
      }

      setRevokingKeyId(keyId)
      try {
        await revokeApiGatewayKey(keyId)
        toast.success('API Gateway key revoked.')
        await loadKeys()
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to revoke API Gateway key.'
        toast.error(message)
      } finally {
        setRevokingKeyId(null)
      }
    },
    [keys, loadKeys]
  )

  const canCreateKey =
    apiGatewayAccess.accessEnabled &&
    Boolean(activeTenant?.id) &&
    !permissionFamiliesLoading &&
    !permissionFamilyError &&
    permissionFamilyOptions.length > 0

  return (
    <>
      <ApiGatewayManagementWorkspace
        mode="portal"
        title="API Access"
        description="Create and manage tenant-scoped API keys for services exposed through the API Gateway."
        eyebrow="API Gateway"
        contextSummary={{
          eyebrow: activeOrganization?.name ?? 'Tenant scope',
          title: activeTenant?.name ?? 'API Access',
          description: activeTenant?.id
            ? `Keys created here are scoped to ${activeTenant.name ?? activeTenant.id}.`
            : 'API Gateway keys require an active tenant context.',
          badges: [
            {
              id: 'feature-status',
              label: apiGatewayAccess.apiGatewayFeatureEnabled ? 'Feature enabled' : 'Feature disabled',
              tone: apiGatewayAccess.apiGatewayFeatureEnabled ? 'success' : 'warning',
            },
            {
              id: 'permission-status',
              label: apiGatewayAccess.apiGatewayPermissionGranted ? 'Access granted' : 'Access denied',
              tone: apiGatewayAccess.apiGatewayPermissionGranted ? 'success' : 'danger',
            },
          ],
          items: activeTenant?.id
            ? [
                { id: 'tenant-id', label: 'Tenant ID', value: activeTenant.id },
                ...(activeOrganization?.name
                  ? [{ id: 'organization', label: 'Organization', value: activeOrganization.name }]
                  : []),
              ]
            : [],
        }}
        notices={notices}
        stats={stats}
        docsActions={docsActions}
        keys={mappedKeys}
        loading={loading}
        errorTitle={error ? 'Unable to load API Gateway keys' : null}
        errorDescription={error}
        emptyTitle={
          !activeTenant?.id
            ? 'Select a tenant to manage API Gateway keys'
            : !apiGatewayAccess.apiGatewayFeatureEnabled
              ? 'API Gateway is disabled for this tenant'
              : !apiGatewayAccess.apiGatewayPermissionGranted
                ? 'You do not have permissions'
                : 'No API Gateway keys yet'
        }
        emptyDescription={
          !activeTenant?.id
            ? 'Choose an active tenant from the portal shell before creating API keys.'
            : !apiGatewayAccess.apiGatewayFeatureEnabled
              ? 'The tenant feature `api.gateway.enabled` must be enabled before this workspace becomes available.'
              : !apiGatewayAccess.apiGatewayPermissionGranted
                ? 'You can see this section because the tenant feature is enabled, but your tenant role does not grant API Gateway key management.'
                : permissionFamilyError
                  ? 'The permission catalog is unavailable right now, so key creation is temporarily disabled.'
                  : 'Create a key for an automated agent and copy the secret when it is revealed.'
        }
        selectedKeyId={selectedKeyId}
        createActionLabel={canCreateKey ? 'Create key' : null}
        revokingKeyId={revokingKeyId}
        onDocsAction={handleDocsAction}
        onSelectKey={setSelectedKeyId}
        onCreateKey={canCreateKey ? () => setCreateOpen(true) : undefined}
        onRequestRevoke={apiGatewayAccess.accessEnabled && activeTenant?.id ? handleRevoke : undefined}
      />

      <ApiGatewayKeyCreateDialog
        open={createOpen}
        mode="portal"
        title="Create API Gateway key"
        description="Generate a tenant-scoped API key for an automated agent or integration."
        helperText="Manage access also grants read access for the same route family."
        draft={createDraft}
        validation={createValidation}
        permissionFamilies={permissionFamilyOptions}
        submitting={submitting}
        onOpenChange={open => {
          setCreateOpen(open)
          if (!open) {
            setCreateDraft(createEmptyDraft())
            setCreateValidation({})
          }
        }}
        onDraftChange={draft => {
          setCreateDraft(draft)
          setCreateValidation({})
        }}
        onSubmit={handleCreateSubmit}
      />

      <ApiGatewaySecretRevealDialog
        open={secretState.open}
        displayName={secretState.displayName}
        prefix={secretState.prefix}
        secret={secretState.secret}
        copied={secretState.copied}
        onOpenChange={open => {
          setSecretState(current => ({ ...current, open }))
        }}
        onCopy={async () => {
          try {
            await copyText(secretState.secret)
            setSecretState(current => ({ ...current, copied: true }))
            toast.success('Secret copied.')
          } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to copy secret.')
          }
        }}
        onAcknowledge={() => {
          setSecretState({
            open: false,
            displayName: '',
            prefix: '',
            secret: '',
            copied: false,
          })
        }}
      />
    </>
  )
}
